import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { ArrowLeftRight, CheckCircle2, ImageOff, ShieldCheck } from 'lucide-react';
import { StatusMessage } from '../../ui/StatusMessage';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../config/featureFlags';

const getTitle = (item) => item?.assetName || item?.productName || 'Trade item';
const getDescription = (item) => item?.assetDescription || item?.productDescription || 'No description provided.';
const getBtcValue = (item) => {
    const value = Number(item?.salePrice);
    return Number.isFinite(value) && value > 0 ? value : null;
};
const formatBtcValue = (value) => (
    Number.isFinite(value) ? `${value.toFixed(8)} BTC` : 'No BTC value listed'
);

const TradeConfirmationModal = ({
    show,
    onHide,
    selectedItems,
    selectedAsset,
    onConfirm,
    onEdit,
    confirming = false,
    error = null,
}) => {
    const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience);
    const handleConfirm = async () => {
        if (typeof onConfirm === 'function') {
            onConfirm();
        }
    };

    const requestedTitle = selectedAsset?.assetName || selectedAsset?.productName || 'Marketplace listing';
    const offeredKnownValues = selectedItems
        .map(getBtcValue)
        .filter((value) => value !== null);
    const offeredKnownTotal = offeredKnownValues.reduce((sum, value) => sum + value, 0);
    const requestedKnownValue = getBtcValue(selectedAsset);
    const hasComparableValues = offeredKnownValues.length > 0 && requestedKnownValue !== null;
    const valueDelta = hasComparableValues ? offeredKnownTotal - requestedKnownValue : null;
    const valueMessage = !bitcoinEnabled ? 'Compare usefulness, condition, delivery effort, and trust before submitting.' : hasComparableValues
        ? valueDelta >= 0
            ? `Your listed offer value is ${formatBtcValue(valueDelta)} above the requested listing.`
            : `Your listed offer value is ${formatBtcValue(Math.abs(valueDelta))} below the requested listing.`
        : 'Some items do not have BTC values, so compare usefulness, condition, delivery effort, and trust before submitting.';

    return (
        <Modal show={show} onHide={onHide} size="xl" centered contentClassName="marketplace-flow-modal">
            <Modal.Header closeButton className="marketplace-flow-header">
                <Modal.Title>
                    <span className="marketplace-flow-kicker"><ArrowLeftRight className="h-4 w-4" /> Final review</span>
                    Confirm trade offer
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="marketplace-flow-body">
                <div className="marketplace-confirm-grid">
                    <section className="marketplace-confirm-panel">
                        <span className="marketplace-confirm-label">You offer</span>
                        <div className="marketplace-confirm-value">
                            <strong>{selectedItems.length} item{selectedItems.length === 1 ? '' : 's'}</strong>
                            {bitcoinEnabled && <span>{offeredKnownValues.length ? `Known value ${formatBtcValue(offeredKnownTotal)}` : 'No BTC values listed'}</span>}
                        </div>
                        <div className="marketplace-confirm-items">
                            {selectedItems.map((item, index) => (
                                <article key={item.id || index} className="marketplace-confirm-item">
                                    {item.imageURL ? (
                                        <img src={item.imageURL} alt={item.assetName || 'Selected trade item'} />
                                    ) : (
                                        <span className="marketplace-confirm-placeholder"><ImageOff className="h-6 w-6" /></span>
                                    )}
                                    <span className="marketplace-confirm-copy">
                                        <strong>{getTitle(item)}</strong>
                                        <span>{getDescription(item)}</span>
                                        {bitcoinEnabled && <em>{formatBtcValue(getBtcValue(item))}</em>}
                                    </span>
                                </article>
                            ))}
                        </div>
                    </section>

                    <div className="marketplace-confirm-swap">
                        <ArrowLeftRight className="h-6 w-6" />
                    </div>

                    <section className="marketplace-confirm-panel">
                        <span className="marketplace-confirm-label">You request</span>
                        <div className="marketplace-confirm-value">
                            <strong>1 item</strong>
                            {bitcoinEnabled && <span>{formatBtcValue(requestedKnownValue)}</span>}
                        </div>
                        <article className="marketplace-confirm-item is-target">
                            {selectedAsset?.imageURL ? (
                                <img src={selectedAsset.imageURL} alt={requestedTitle} />
                            ) : (
                                <span className="marketplace-confirm-placeholder"><ImageOff className="h-6 w-6" /></span>
                            )}
                            <span className="marketplace-confirm-copy">
                                <strong>{requestedTitle}</strong>
                                <span>{getDescription(selectedAsset)}</span>
                                {bitcoinEnabled && <em>{formatBtcValue(requestedKnownValue)}</em>}
                            </span>
                        </article>
                    </section>
                </div>
                <div className={hasComparableValues && Math.abs(valueDelta) < 0.00000001 ? 'marketplace-value-context is-balanced' : 'marketplace-value-context'}>
                    <strong>Trade value context</strong>
                    <span>{valueMessage}</span>
                </div>
                <div className="marketplace-flow-note">
                    <ShieldCheck className="h-5 w-5" />
                    <span>This submits an offer for the other member to review. No exchange is finalized until the trade is accepted and confirmed.</span>
                </div>
                {confirming && (
                    <StatusMessage
                        tone="loading"
                        title="Submitting trade offer"
                        message="We are sending your trade offer."
                    />
                )}
                {error && (
                    <StatusMessage
                        tone="error"
                        title="Could not submit trade offer"
                        message={error}
                    />
                )}
            </Modal.Body>
            <Modal.Footer className="marketplace-flow-footer">
                <Button variant="outline-light" onClick={onEdit} disabled={confirming} className="marketplace-secondary-action">
                    Edit offer
                </Button>
                <Button variant="success" onClick={handleConfirm} disabled={confirming} className="marketplace-primary-action">
                    {confirming ? 'Submitting offer...' : <><CheckCircle2 className="h-4 w-4" /> Submit trade offer</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TradeConfirmationModal;
